import { View, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { screens } from '@chillout/core'
import { Button, TextField } from '@chillout/ui'
import type { CardIconKey } from '@chillout/ui'
import { PerMealInput } from './PerMealInput'
import { StampPreview } from './StampPreview'
import type { StampMilestoneIcon } from './useStampSettings'

interface PerMealSectionProps {
  mealsCount: number
  onMealsCountChange: (value: number) => void
  maxStampsPerTransaction: number
  currentStamps?: number
  stampTarget: number
  completionCardIcon: CardIconKey
  milestones: StampMilestoneIcon[]
  receiptReferenceEnabled: boolean
  receiptReference: string
  onReceiptReferenceChange: (value: string) => void
  onAddStamps: () => void
}

export function PerMealSection({
  mealsCount,
  onMealsCountChange,
  maxStampsPerTransaction,
  currentStamps,
  stampTarget,
  completionCardIcon,
  milestones,
  receiptReferenceEnabled,
  receiptReference,
  onReceiptReferenceChange,
  onAddStamps,
}: PerMealSectionProps) {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const twoColumn = width >= screens.lg

  const buttonLabel = t('stampAwarding.addStampsAction')

  if (twoColumn) {
    return (
      <View style={{ flexDirection: 'row', gap: 32 }}>
        {/* Left column: stamp preview */}
        <View style={{ flex: 1 }}>
          {currentStamps !== undefined && (
            <StampPreview
              currentStamps={currentStamps}
              stampDelta={mealsCount}
              stampTarget={stampTarget}
              completionCardIcon={completionCardIcon}
              milestones={milestones}
            />
          )}
        </View>

        {/* Right column: stepper + button */}
        <View style={{ flex: 1 }} className="gap-4">
          <PerMealInput value={mealsCount} onChange={onMealsCountChange} max={maxStampsPerTransaction} />
          <View className="mt-auto gap-3">
            {receiptReferenceEnabled && (
              <TextField
                label={t('stampAwarding.receiptReference.label')}
                placeholder={t('stampAwarding.receiptReference.placeholder')}
                value={receiptReference}
                onChangeText={onReceiptReferenceChange}
                returnKeyType="done"
              />
            )}
            <Button label={buttonLabel} onPress={onAddStamps} className="py-8" />
          </View>
        </View>
      </View>
    )
  }

  return (
    <View className="gap-4">
      <PerMealInput value={mealsCount} onChange={onMealsCountChange} max={maxStampsPerTransaction} />
      {currentStamps !== undefined && (
        <StampPreview
          currentStamps={currentStamps}
          stampDelta={mealsCount}
          stampTarget={stampTarget}
          completionCardIcon={completionCardIcon}
          milestones={milestones}
        />
      )}
      {receiptReferenceEnabled && (
        <TextField
          label={t('stampAwarding.receiptReference.label')}
          placeholder={t('stampAwarding.receiptReference.placeholder')}
          value={receiptReference}
          onChangeText={onReceiptReferenceChange}
          returnKeyType="done"
        />
      )}
      <Button label={buttonLabel} onPress={onAddStamps} className="py-8" />
    </View>
  )
}